import { useState } from "react";
import { Menu, X, MessageSquare, Phone, Mail, Workflow } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose} from "@/components/ui/sheet";
import { useLanguage } from "@/contexts/LanguageContext";

type Props = {
  onContactOpen?: () => void;
};

export default function MobileNav({ onContactOpen }: Props) {
  const { language, setLanguage, t } = useLanguage();
  const [open, setOpen] = useState(false);

  const products =
    language === "is"
      ? [
          { name: "SpjallFulltrúi", href: "/is/products/chat", icon: MessageSquare },
          { name: "RaddFulltrúi", href: "/is/products/voice", icon: Phone },
          { name: "PóstFulltrúi", href: "/is/products/email", icon: Mail },
          { name: "FlæðiFulltrúi", href: "/is/products/automation", icon: Workflow },
        ]
      : [
          { name: "Chat Fulltrúi", href: "/en/products/chat", icon: MessageSquare },
          { name: "Voice Fulltrúi", href: "/en/products/voice", icon: Phone },
          { name: "Email Fulltrúi", href: "/en/products/email", icon: Mail },
          { name: "Automation Fulltrúi", href: "/en/products/automation", icon: Workflow },
        ];

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button aria-label="Open navigation" variant="ghost" className="md:hidden">
          <Menu className="h-6 w-6" />
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[260px] sm:w-[320px]">
        {/* Custom close button with hover effect */}
        <SheetClose asChild>
          <Button
            aria-label="Close menu"
            variant="ghost"
            className="absolute right-8 top-4 rounded-md hover:bg-accent"
          >
            <X className="h-5 w-5" />
          </Button>
        </SheetClose>

        <div className="flex items-center justify-between mb-4">
          <div className="text-lg font-medium">{t.nav.products}</div>
        </div>

        <nav className="flex flex-col gap-2">
          {products.map((p) => {
            const Icon = p.icon;
            return (
              <a
                key={p.href}
                href={p.href}
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 rounded-md p-3 hover:bg-accent"
              >
                <Icon className="h-4 w-4 text-primary" />
                <span>{p.name}</span>
              </a>
            );
          })}

          <hr className="my-2" />

          <button
            onClick={() => {
              setOpen(false);
              onContactOpen?.();
            }}
            className="text-left rounded-md p-3 hover:bg-accent"
          >
            {t.nav.contact}
          </button>

          <a
            href={`/${language}/about`}
            onClick={() => setOpen(false)}
            className="rounded-md p-3 hover:bg-accent"
          >
            {t.nav.about}
          </a>

          <hr className="my-2" />

          {/* Language Toggle Button */}
          <div className="p-3">
            <div className="inline-flex w-full rounded-md border border-input bg-background p-1 gap-1">
              <button
                onClick={() => setLanguage("is")}
                className={`flex-1 px-3 py-1.5 text-sm font-medium transition-colors rounded ${
                  language === "is"
                    ? "bg-accent text-accent-foreground"
                    : "bg-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                IS
              </button>
              <button
                onClick={() => setLanguage("en")}
                className={`flex-1 px-3 py-1.5 text-sm font-medium transition-colors rounded ${
                  language === "en"
                    ? "bg-accent text-accent-foreground"
                    : "bg-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                EN
              </button>
            </div>
          </div>
        </nav>
      </SheetContent>
    </Sheet>
  );
}
